import { ReactNode, useState } from 'react';

import {
  Avatar,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  FAIconName,
  Icon,
  Popover,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import {
  getExactAgeFromDOB,
  getName,
  getPreferredFirstName,
} from '~/lib/util/patient.util';
import { ProviderAlert } from '~/lib/types/providerDashboard';

const DOB_FORMAT = 'yyyy-MM-dd';

interface AlertPatientPopoverProps {
  alert: ProviderAlert;
  fullName: string;
  triggerContent: ReactNode;
}

const AlertPatientPopover = ({
  alert,
  fullName,
  triggerContent,
}: AlertPatientPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    alertId,
    patientFirstName,
    patientChosenName,
    patientDateOfBirth,
    patientGeographicState,
  } = alert;

  const preferredFirst = getPreferredFirstName(
    patientChosenName ?? '',
    patientFirstName ?? '',
    false,
  );
  const legalFirstName = getName(patientFirstName ?? '');
  const showLegalName = preferredFirst !== legalFirstName && !!legalFirstName;
  const dob = patientDateOfBirth
    ? DateTime.fromFormat(patientDateOfBirth, DOB_FORMAT)
    : null;
  const age = patientDateOfBirth
    ? getExactAgeFromDOB(patientDateOfBirth, DOB_FORMAT, 'long')
    : null;
  const dobDisplay = dob?.isValid
    ? `${dob.toFormat('LL/dd/yyyy')}${age ? ` (${age})` : ''}`
    : '';

  return (
    <Popover
      id={`alert-patient-popover-${alertId}`}
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      trigger="hover"
      placement="bottom-start"
      size="md"
      minWidth="280px"
      triggerContent={<ChakraBox>{triggerContent}</ChakraBox>}
      bodyContent={
        <ChakraFlex
          data-testid={`alert-patient-popover-body-${alertId}`}
          alignItems="center"
          gap="14px"
        >
          <Avatar name={fullName} size="md" userType="patient" />
          <ChakraFlex direction="column" gap="6px" minWidth="0">
            <ChakraFlex alignItems="baseline" flexWrap="wrap" gap="6px">
              <ChakraText
                fontSize="16px"
                fontWeight="500"
                color="neutral.primary"
              >
                {fullName}
              </ChakraText>
              {showLegalName && (
                <ChakraText fontSize="13px" color="neutral.secondary">
                  ({legalFirstName})
                </ChakraText>
              )}
            </ChakraFlex>
            {(dobDisplay || patientGeographicState) && (
              <ChakraFlex alignItems="center" flexWrap="wrap" gap="16px">
                {dobDisplay && (
                  <ChakraFlex alignItems="center" gap="6px">
                    <Icon
                      name={FAIconName.calendarSolid}
                      color="neutral.secondary"
                      size="sm"
                    />
                    <ChakraText fontSize="13px" color="neutral.primary">
                      {dobDisplay}
                    </ChakraText>
                  </ChakraFlex>
                )}
                {patientGeographicState && (
                  <ChakraFlex alignItems="center" gap="6px">
                    <Icon
                      name={FAIconName.mapMarkerAltSolid}
                      color="neutral.secondary"
                      size="sm"
                    />
                    <ChakraText fontSize="13px" color="neutral.primary">
                      {patientGeographicState}
                    </ChakraText>
                  </ChakraFlex>
                )}
              </ChakraFlex>
            )}
          </ChakraFlex>
        </ChakraFlex>
      }
    />
  );
};

export default AlertPatientPopover;
